/**
 * Upload Interface Type Definitions
 *
 * This file contains all TypeScript type definitions for the upload interface,
 * including file uploads, upload jobs, progress tracking, and SSE events.
 *
 * @module upload.types
 */

/**
 * Status of an individual file during upload
 */
export type UploadFileStatus =
  | 'pending'       // Waiting to upload
  | 'uploading'     // Currently uploading
  | 'validating'    // Server-side validation
  | 'completed'     // Successfully uploaded
  | 'failed'        // Upload or validation failed
  | 'cancelled';    // Upload cancelled by user

/**
 * Status of an upload job
 */
export type UploadJobStatus =
  | 'pending'       // Job created, not started
  | 'uploading'     // Files being uploaded
  | 'validating'    // Server validating files
  | 'completed'     // All files uploaded and validated
  | 'failed'        // Job failed
  | 'cancelled';    // Job cancelled by user

/**
 * Supported file formats for upload
 */
export type FileFormat =
  | 'parquet'
  | 'csv'
  | 'json'
  | 'avro'
  | 'orc'
  | 'excel';

/**
 * Extended File object with upload metadata
 *
 * This extends the native File interface with additional properties
 * needed for tracking upload progress and status in the UI.
 */
export interface LocalFile extends File {
  /**
   * Unique identifier for this file (UUID)
   */
  id: string;

  /**
   * Preview URL for the file (optional)
   * Can be used for image thumbnails or file icons
   */
  preview?: string;

  /**
   * Upload progress percentage (0-100)
   */
  uploadProgress?: number;

  /**
   * Current upload status
   */
  status?: UploadFileStatus;

  /**
   * Error message if upload failed
   */
  error?: string;

  /**
   * Detected file format
   */
  format?: FileFormat;
}

/**
 * Upload job information
 *
 * Represents a batch upload job that can contain multiple files.
 */
export interface UploadJob {
  /**
   * Unique job identifier (UUID)
   */
  id: string;

  /**
   * Target dataset name
   */
  dataset_name: string;

  /**
   * Current job status
   */
  status: UploadJobStatus;

  /**
   * Overall progress percentage (0-100)
   */
  progress_percentage: number;

  /**
   * Total number of files in this job
   */
  total_files: number;

  /**
   * Number of files successfully uploaded
   */
  completed_files: number;

  /**
   * Number of files that failed
   */
  failed_files: number;

  /**
   * Total size of all files in bytes
   */
  total_size?: number;

  /**
   * Number of bytes uploaded so far
   */
  uploaded_size?: number;

  /**
   * Whether to validate files against schema
   */
  validate_schema: boolean;

  /**
   * Error message if job failed
   */
  error_message?: string;

  /**
   * Job creation timestamp (ISO 8601)
   */
  created_at: string;

  /**
   * Last update timestamp (ISO 8601)
   */
  updated_at: string;

  /**
   * User who created the job
   */
  created_by: string;

  /**
   * Job completion timestamp (ISO 8601)
   */
  completed_at?: string;
}

/**
 * Individual file within an upload job
 *
 * Server-side representation of a file being uploaded.
 */
export interface UploadFile {
  /**
   * Unique file identifier (UUID)
   */
  id: string;

  /**
   * Upload job ID this file belongs to
   */
  upload_job_id: string;

  /**
   * Original filename
   */
  filename: string;

  /**
   * File size in bytes
   */
  file_size: number;

  /**
   * File format
   */
  file_format: FileFormat;

  /**
   * Current upload status
   */
  status: UploadFileStatus;

  /**
   * Upload progress percentage (0-100)
   */
  progress_percentage: number;

  /**
   * Number of rows in the file
   */
  row_count?: number;

  /**
   * Number of rows that failed validation
   */
  failed_rows?: number;

  /**
   * Error message if upload failed
   */
  error_message?: string;

  /**
   * Upload start timestamp (ISO 8601)
   */
  started_at?: string;

  /**
   * Upload completion timestamp (ISO 8601)
   */
  completed_at?: string;
}

/**
 * Progress event from Server-Sent Events (SSE) stream
 *
 * Real-time progress updates for an upload job.
 */
export interface UploadProgressEvent {
  /**
   * Event type for SSE message classification
   */
  event: 'progress' | 'complete' | 'error';

  /**
   * Upload job ID
   */
  upload_job_id: string;

  /**
   * Current job status
   */
  status: UploadJobStatus;

  /**
   * Overall progress percentage (0-100)
   */
  progress_percentage: number;

  /**
   * Total files in job
   */
  total_files: number;

  /**
   * Files completed
   */
  completed_files: number;

  /**
   * Files failed
   */
  failed_files: number;

  /**
   * Current file being processed
   */
  current_file?: string;

  /**
   * Individual file progress updates
   */
  files: UploadFile[];

  /**
   * Error message if job failed
   */
  error_message?: string;

  /**
   * Event timestamp (ISO 8601)
   */
  timestamp: string;
}

/**
 * Request to create a new upload job
 */
export interface CreateUploadJobRequest {
  /**
   * Target dataset name (existing or new)
   */
  dataset_name: string;

  /**
   * Total number of files to upload
   */
  total_files: number;

  /**
   * Whether to validate files against schema
   * @default true
   */
  validate_schema?: boolean;

  /**
   * Expected total size in bytes (optional)
   */
  total_size?: number;
}

/**
 * Response from creating an upload job
 */
export interface CreateUploadJobResponse {
  /**
   * Created upload job
   */
  upload_job: UploadJob;

  /**
   * Pre-signed URLs for file uploads (if using cloud storage)
   */
  upload_urls?: Record<string, string>;
}

/**
 * Request to complete an upload job
 */
export interface CompleteUploadJobRequest {
  /**
   * Final job status
   */
  status: 'completed' | 'failed' | 'cancelled';

  /**
   * Error message if job failed
   */
  error_message?: string;
}

/**
 * Response from completing an upload job
 */
export interface CompleteUploadJobResponse {
  /**
   * Updated upload job
   */
  upload_job: UploadJob;

  /**
   * Link to view the dataset
   */
  dataset_url?: string;
}

/**
 * Dataset information for selector
 */
export interface Dataset {
  /**
   * Dataset unique identifier
   */
  id: string;

  /**
   * Dataset name (unique)
   */
  name: string;

  /**
   * Dataset description
   */
  description?: string;

  /**
   * Number of files in dataset
   */
  file_count: number;

  /**
   * Total size in bytes
   */
  total_size: number;

  /**
   * Dataset schema (if defined)
   */
  schema?: Record<string, any>;

  /**
   * Creation timestamp (ISO 8601)
   */
  created_at: string;

  /**
   * Last update timestamp (ISO 8601)
   */
  updated_at: string;
}

/**
 * Dataset type - matches backend DatasetType enum
 */
export type DatasetType = 'table' | 'view' | 'materialized_view' | 'external';

/**
 * Dataset format - matches backend DatasetFormat enum
 */
export type DatasetFormat = 'parquet' | 'csv' | 'json' | 'avro' | 'orc' | 'delta' | 'iceberg';

/**
 * Request to create a new dataset
 */
export interface CreateDatasetRequest {
  /**
   * Dataset name (must be unique)
   */
  name: string;

  /**
   * Fully qualified dataset name (auto-generated)
   */
  qualified_name: string;

  /**
   * Dataset type
   */
  type: DatasetType;

  /**
   * Storage location URI (auto-generated)
   */
  location: string;

  /**
   * Storage format
   */
  format: DatasetFormat;

  /**
   * Dataset description (optional)
   */
  description?: string;

  /**
   * Dataset tags for categorization
   */
  tags?: string[];

  /**
   * Associated schema ID (optional)
   */
  schema_id?: string;
}

/**
 * File upload progress tracking
 */
export interface FileUploadProgress {
  /**
   * File ID
   */
  fileId: string;

  /**
   * Upload progress percentage (0-100)
   */
  progress: number;

  /**
   * Current status
   */
  status: UploadFileStatus;

  /**
   * Error message if failed
   */
  error?: string;

  /**
   * Number of bytes uploaded
   */
  uploadedBytes?: number;

  /**
   * Total file size in bytes
   */
  totalBytes?: number;
}

/**
 * File validation result
 */
export interface FileValidationResult {
  /**
   * Whether file is valid
   */
  valid: boolean;

  /**
   * List of validation errors
   */
  errors: string[];

  /**
   * File metadata
   */
  metadata?: {
    /**
     * Detected file format
     */
    format: FileFormat;

    /**
     * File size in bytes
     */
    size: number;

    /**
     * File MIME type
     */
    mimeType: string;
  };
}

/**
 * Options for file dropzone
 */
export interface FileDropzoneOptions {
  /**
   * Accepted file extensions (comma-separated)
   * @default ".parquet,.csv,.json"
   */
  accept?: string;

  /**
   * Maximum file size in bytes
   * @default 104857600 (100MB)
   */
  maxSize?: number;

  /**
   * Maximum number of files
   * @default 100
   */
  maxFiles?: number;

  /**
   * Whether to accept multiple files
   * @default true
   */
  multiple?: boolean;

  /**
   * Whether dropzone is disabled
   * @default false
   */
  disabled?: boolean;
}

/**
 * UI state for file upload progress
 */
export interface UploadProgressState {
  /**
   * Map of file ID to progress
   */
  files: Record<string, FileUploadProgress>;

  /**
   * Overall progress percentage
   */
  overallProgress: number;

  /**
   * Whether upload is in progress
   */
  isUploading: boolean;

  /**
   * Number of files completed
   */
  completedCount: number;

  /**
   * Number of files failed
   */
  failedCount: number;

  /**
   * Total files
   */
  totalCount: number;
}

// ============================================================================
// UI Component Props Types
// ============================================================================

/**
 * Props for FileDropzone component
 */
export interface FileDropzoneProps extends FileDropzoneOptions {
  /**
   * Callback when files are selected
   */
  onFilesSelected: (files: LocalFile[]) => void;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Child content (optional)
   */
  children?: React.ReactNode;
}

/**
 * Props for DatasetSelector component
 */
export interface DatasetSelectorProps {
  /**
   * Current dataset configuration value
   */
  value: CreateDatasetRequest | null;

  /**
   * Callback when value changes
   */
  onChange: (dataset: CreateDatasetRequest | null) => void;

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Whether selector is disabled
   */
  disabled?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Props for FileList component
 */
export interface FileListProps {
  /**
   * Array of files to display
   */
  files: LocalFile[];

  /**
   * Callback when file is removed
   */
  onRemove?: (fileId: string) => void;

  /**
   * Whether file removal is disabled
   */
  disabled?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Props for UploadProgress component
 */
export interface UploadProgressProps {
  /**
   * Current upload job data (can be either full UploadJob or SSE progress event)
   */
  uploadJob: UploadJob | UploadProgressEvent | null;

  /**
   * List of files in the job
   */
  files?: UploadFile[];

  /**
   * Callback for cancel button
   */
  onCancel?: () => void;

  /**
   * Callback for completion button
   */
  onComplete?: () => void;

  /**
   * SSE connection status
   */
  isConnected?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

// ============================================================================
// Constants and UI Mappings
// ============================================================================

/**
 * Human-readable labels for upload file statuses
 */
export const UPLOAD_FILE_STATUS_LABELS: Record<UploadFileStatus, string> = {
  pending: 'Pending',
  uploading: 'Uploading',
  validating: 'Validating',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/**
 * Human-readable labels for upload job statuses
 */
export const UPLOAD_JOB_STATUS_LABELS: Record<UploadJobStatus, string> = {
  pending: 'Pending',
  uploading: 'Uploading',
  validating: 'Validating',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/**
 * Tailwind CSS classes for upload file status badges
 */
export const UPLOAD_FILE_STATUS_COLORS: Record<UploadFileStatus, string> = {
  pending: 'bg-muted text-muted-foreground',
  uploading: 'bg-info text-info-foreground animate-pulse',
  validating: 'bg-violet text-violet-foreground animate-pulse',
  completed: 'bg-success text-success-foreground',
  failed: 'bg-destructive text-destructive-foreground',
  cancelled: 'bg-muted text-muted-foreground',
};

/**
 * Tailwind CSS classes for upload job status badges
 */
export const UPLOAD_JOB_STATUS_COLORS: Record<UploadJobStatus, string> = {
  pending: 'bg-muted text-muted-foreground',
  uploading: 'bg-info text-info-foreground animate-pulse',
  validating: 'bg-violet text-violet-foreground animate-pulse',
  completed: 'bg-success text-success-foreground',
  failed: 'bg-destructive text-destructive-foreground',
  cancelled: 'bg-muted text-muted-foreground',
};

/**
 * File format to MIME type mapping
 */
export const FILE_FORMAT_MIME_TYPES: Record<FileFormat, string[]> = {
  parquet: ['application/parquet', 'application/octet-stream'],
  csv: ['text/csv', 'text/plain', 'application/csv'],
  json: ['application/json', 'text/json'],
  avro: ['application/avro', 'application/octet-stream'],
  orc: ['application/orc', 'application/octet-stream'],
  excel: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
};

/**
 * File format to extension mapping
 */
export const FILE_FORMAT_EXTENSIONS: Record<FileFormat, string[]> = {
  parquet: ['.parquet'],
  csv: ['.csv', '.txt'],
  json: ['.json', '.jsonl', '.ndjson'],
  avro: ['.avro'],
  orc: ['.orc'],
  excel: ['.xls', '.xlsx'],
};

/**
 * Default accept string for file dropzone (all supported formats)
 */
export const DEFAULT_ACCEPT_FORMATS = '.parquet,.csv,.json,.avro,.orc,.xls,.xlsx';

/**
 * Default maximum file size (100MB)
 */
export const DEFAULT_MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes

/**
 * Default maximum number of files
 */
export const DEFAULT_MAX_FILES = 100;

/**
 * Type guard to check if a status is an active upload status
 */
export function isActiveUploadStatus(status: UploadFileStatus | UploadJobStatus): boolean {
  return status === 'pending' || status === 'uploading' || status === 'validating';
}

/**
 * Type guard to check if a status is a terminal status
 */
export function isTerminalUploadStatus(status: UploadFileStatus | UploadJobStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

/**
 * Type guard to check if LocalFile has required upload properties
 */
export function isUploadableFile(file: File): file is LocalFile {
  return 'id' in file;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

/**
 * Detect file format from filename
 */
export function detectFileFormat(filename: string): FileFormat | null {
  const extension = filename.toLowerCase().split('.').pop();
  if (!extension) return null;

  for (const [format, extensions] of Object.entries(FILE_FORMAT_EXTENSIONS)) {
    if (extensions.some(ext => ext.substring(1) === extension)) {
      return format as FileFormat;
    }
  }

  return null;
}

/**
 * Validate file against dropzone options
 */
export function validateFile(
  file: File,
  options: FileDropzoneOptions = {}
): FileValidationResult {
  const errors: string[] = [];
  const {
    accept = DEFAULT_ACCEPT_FORMATS,
    maxSize = DEFAULT_MAX_FILE_SIZE,
  } = options;

  // Check file size
  if (file.size > maxSize) {
    errors.push(`File size (${formatFileSize(file.size)}) exceeds maximum allowed size (${formatFileSize(maxSize)})`);
  }

  // Check file extension
  const acceptedExtensions = accept.split(',').map(ext => ext.trim().toLowerCase());
  const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

  if (!acceptedExtensions.includes(fileExtension)) {
    errors.push(`File type "${fileExtension}" is not accepted. Allowed types: ${accept}`);
  }

  // Detect format
  const format = detectFileFormat(file.name);

  return {
    valid: errors.length === 0,
    errors,
    metadata: {
      format: format || 'csv', // Default to CSV if cannot detect
      size: file.size,
      mimeType: file.type,
    },
  };
}
